import { defineNuxtPlugin } from 'nuxt/app';
import { onScopeDispose, watch } from 'vue';

const KEEP_ALIVE_INTERVAL_MS = 25 * 60 * 1000;

export default defineNuxtPlugin(() => {
  if (!import.meta.client) {
    return;
  }

  const { loggedIn, syncStatus, silentRefresh } = useShopAuth();
  const { notifySessionExpired } = useSessionExpiryNotice();
  const logger = useLogger();

  let intervalId: ReturnType<typeof setInterval> | undefined;

  async function refreshSessionAndStatus(): Promise<void> {
    if (!loggedIn.value) {
      return;
    }

    const wasLoggedIn = loggedIn.value;

    try {
      const stillLoggedIn = await silentRefresh();
      await syncStatus();

      if (wasLoggedIn && !stillLoggedIn) {
        notifySessionExpired();
      }
    } catch (error) {
      logger.error(
        'Session keep-alive',
        'Failed to refresh OIDC session',
        error
      );
    }
  }

  function startKeepAlive(): void {
    if (intervalId !== undefined) {
      return;
    }

    intervalId = setInterval(() => {
      void refreshSessionAndStatus();
    }, KEEP_ALIVE_INTERVAL_MS);
  }

  function stopKeepAlive(): void {
    if (intervalId === undefined) {
      return;
    }

    clearInterval(intervalId);
    intervalId = undefined;
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      void refreshSessionAndStatus();
    }
  }

  function onOnline(): void {
    void refreshSessionAndStatus();
  }

  // ponytail: boot syncStatus lives in shopAuthStatus.client — avoid duplicate
  if (loggedIn.value) {
    startKeepAlive();
  }

  watch(loggedIn, (isLoggedIn) => {
    if (isLoggedIn) {
      startKeepAlive();
      void syncStatus();
      return;
    }

    stopKeepAlive();
  });

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', onOnline);

  onScopeDispose(() => {
    stopKeepAlive();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('online', onOnline);
  });
});
